<template>
  <section class="section container">
    <div class="container mt-0 mb-6">
      <h1 class="title is-4 mb-3">{{ $t("credit analysis reports") }}</h1>
    </div>

    <!-- Report Filters -->
    <div class="box">
      <h2 class="title is-5">{{ $t("report filters") }}</h2>
      <div class="columns">
        <div class="column is-3">
          <div class="field">
            <label class="label">{{ $t("start date") }}</label>
            <input type="date" class="input" v-model="filters.start_date" />
          </div>
        </div>
        <div class="column is-3">
          <div class="field">
            <label class="label">{{ $t("end date") }}</label>
            <input type="date" class="input" v-model="filters.end_date" />
          </div>
        </div>
        <div class="column is-3">
          <div class="field">
            <label class="label">{{ $t("status") }}</label>
            <div class="select is-fullwidth">
              <select v-model="filters.status">
                <option value="">{{ $t("all") }}</option>
                <option value="pending">{{ $t("pending") }}</option>
                <option value="in_progress">{{ $t("in progress") }}</option>
                <option value="completed">{{ $t("completed") }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="column is-3">
          <div class="field">
            <label class="label">&nbsp;</label>
            <button
              class="button is-primary is-fullwidth"
              @click="generateReports"
            >
              <span class="icon"><i class="fa fa-bar-chart"></i></span>
              <span>{{ $t("generate reports") }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Statistics Summary -->
    <div class="columns is-multiline" v-if="reports.summary">
      <div class="column is-3">
        <div class="box has-text-centered">
          <p class="title is-3 has-text-info">
            {{ reports.summary.total_requests || 0 }}
          </p>
          <p class="subtitle is-6">{{ $t("total requests") }}</p>
        </div>
      </div>
      <div class="column is-3">
        <div class="box has-text-centered">
          <p class="title is-3 has-text-warning">
            {{ reports.summary.pending_requests || 0 }}
          </p>
          <p class="subtitle is-6">{{ $t("pending requests") }}</p>
        </div>
      </div>
      <div class="column is-3">
        <div class="box has-text-centered">
          <p class="title is-3 has-text-success">
            {{ reports.summary.approved_count || 0 }}
          </p>
          <p class="subtitle is-6">{{ $t("approved") }}</p>
        </div>
      </div>
      <div class="column is-3">
        <div class="box has-text-centered">
          <p class="title is-3 has-text-danger">
            {{ reports.summary.rejected_count || 0 }}
          </p>
          <p class="subtitle is-6">{{ $t("rejected") }}</p>
        </div>
      </div>
    </div>

    <!-- Report Sections -->
    <div class="tabs is-boxed">
      <ul>
        <li :class="{ 'is-active': activeTab === 'productivity' }">
          <a @click="activeTab = 'productivity'">
            <span class="icon is-small"><i class="fa fa-line-chart"></i></span>
            <span>{{ $t("productivity report") }}</span>
          </a>
        </li>
        <li :class="{ 'is-active': activeTab === 'statistics' }">
          <a @click="activeTab = 'statistics'">
            <span class="icon is-small"><i class="fa fa-pie-chart"></i></span>
            <span>{{ $t("statistics report") }}</span>
          </a>
        </li>
        <li :class="{ 'is-active': activeTab === 'trends' }">
          <a @click="activeTab = 'trends'">
            <span class="icon is-small"><i class="fa fa-area-chart"></i></span>
            <span>{{ $t("trends report") }}</span>
          </a>
        </li>
      </ul>
    </div>

    <!-- Productivity Report -->
    <div v-show="activeTab === 'productivity'" class="tab-content">
      <div class="box">
        <h3 class="title is-5">{{ $t("productivity report") }}</h3>

        <div
          class="table-container"
          v-if="reports.productivity && reports.productivity.length > 0"
        >
          <table class="table is-fullwidth is-striped">
            <thead>
              <tr>
                <th>{{ $t("analyst") }}</th>
                <th>{{ $t("evaluations completed") }}</th>
                <th>{{ $t("average time days") }}</th>
                <th>{{ $t("approval rate") }}</th>
                <th>{{ $t("last activity") }}</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="analyst in reports.productivity"
                :key="analyst.user_id"
              >
                <td>{{ analyst.analyst_name || $t("unknown") }}</td>
                <td>{{ analyst.completed_evaluations || 0 }}</td>
                <td>{{ analyst.avg_processing_time || 0 }}</td>
                <td>{{ analyst.approval_rate || 0 }}%</td>
                <td>{{ formatDate(analyst.last_activity) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="has-text-centered">
          <p>{{ $t("no productivity data available") }}</p>
        </div>
      </div>
    </div>

    <!-- Statistics Report -->
    <div v-show="activeTab === 'statistics'" class="tab-content">
      <div class="box">
        <h3 class="title is-5">{{ $t("statistics report") }}</h3>

        <div class="columns is-multiline">
          <div class="column is-6">
            <div class="box">
              <h4 class="title is-6">{{ $t("requests by status") }}</h4>
              <div v-if="reports.statistics && reports.statistics.by_status">
                <div
                  class="level"
                  v-for="status in reports.statistics.by_status"
                  :key="status.status"
                >
                  <div class="level-left">
                    <div class="level-item">
                      <span class="tag" :class="getStatusClass(status.status)">
                        {{ $t(status.status) }}
                      </span>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item">
                      <strong>{{ status.count }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="column is-6">
            <div class="box">
              <h4 class="title is-6">{{ $t("evaluations by result") }}</h4>
              <div v-if="reports.statistics && reports.statistics.by_result">
                <div
                  class="level"
                  v-for="result in reports.statistics.by_result"
                  :key="result.result"
                >
                  <div class="level-left">
                    <div class="level-item">
                      <span class="tag" :class="getResultClass(result.result)">
                        {{ $t(result.result) }}
                      </span>
                    </div>
                  </div>
                  <div class="level-right">
                    <div class="level-item">
                      <strong>{{ result.count }}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="columns">
          <div class="column">
            <div class="box">
              <h4 class="title is-6">{{ $t("processing time statistics") }}</h4>
              <div
                v-if="reports.statistics && reports.statistics.processing_time"
              >
                <p>
                  <strong>{{ $t("average time") }}:</strong>
                  {{ reports.statistics.processing_time.average || 0 }}
                  {{ $t("days") }}
                </p>
                <p>
                  <strong>{{ $t("minimum time") }}:</strong>
                  {{ reports.statistics.processing_time.minimum || 0 }}
                  {{ $t("days") }}
                </p>
                <p>
                  <strong>{{ $t("maximum time") }}:</strong>
                  {{ reports.statistics.processing_time.maximum || 0 }}
                  {{ $t("days") }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Trends Report -->
    <div v-show="activeTab === 'trends'" class="tab-content">
      <div class="box">
        <h3 class="title is-5">{{ $t("trends report") }}</h3>

        <div v-if="reports.trends && reports.trends.length > 0">
          <div class="table-container">
            <table class="table is-fullwidth is-striped">
              <thead>
                <tr>
                  <th>{{ $t("period") }}</th>
                  <th>{{ $t("requests") }}</th>
                  <th>{{ $t("evaluations") }}</th>
                  <th>{{ $t("approval rate") }}</th>
                  <th>{{ $t("avg processing time") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="trend in reports.trends" :key="trend.period">
                  <td>{{ trend.period }}</td>
                  <td>{{ trend.requests_count || 0 }}</td>
                  <td>{{ trend.evaluations_count || 0 }}</td>
                  <td>{{ trend.approval_rate || 0 }}%</td>
                  <td>{{ trend.avg_processing_time || 0 }} {{ $t("days") }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div v-else class="has-text-centered">
          <p>{{ $t("no trends data available") }}</p>
        </div>
      </div>
    </div>

    <!-- Export Options -->
    <div class="box mt-5">
      <h3 class="title is-5">{{ $t("export options") }}</h3>
      <div class="buttons">
        <button class="button is-info" @click="exportReport('csv')">
          <span class="icon"><i class="fa fa-file-excel-o"></i></span>
          <span>{{ $t("export csv") }}</span>
        </button>
        <button class="button is-primary" @click="exportReport('json')">
          <span class="icon"><i class="fa fa-file-code-o"></i></span>
          <span>{{ $t("export json") }}</span>
        </button>
        <button class="button is-success" @click="exportReport('pdf')">
          <span class="icon"><i class="fa fa-file-pdf-o"></i></span>
          <span>{{ $t("export pdf") }}</span>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import { get, error_message, success_message } from "@/utils";
import moment from "moment";

export default {
  name: "CreditAnalysisReports",
  data() {
    return {
      activeTab: "productivity",
      is_loading: false,
      filters: {
        start_date: moment().subtract(30, "days").format("YYYY-MM-DD"),
        end_date: moment().format("YYYY-MM-DD"),
        status: "",
      },
      reports: {
        summary: null,
        productivity: [],
        statistics: null,
        trends: [],
      },
    };
  },
  methods: {
    generateReports() {
      this.is_loading = true;

      // Simular generación de reportes (reemplazar con llamadas API reales)
      setTimeout(() => {
        this.reports = {
          summary: {
            total_requests: 145,
            pending_requests: 23,
            approved_count: 89,
            rejected_count: 33,
          },
          productivity: [
            {
              user_id: 1,
              analyst_name: "María García",
              completed_evaluations: 45,
              avg_processing_time: 3.2,
              approval_rate: 78.5,
              last_activity: moment().subtract(2, "hours").toISOString(),
            },
            {
              user_id: 2,
              analyst_name: "Carlos López",
              completed_evaluations: 38,
              avg_processing_time: 4.1,
              approval_rate: 82.1,
              last_activity: moment().subtract(5, "hours").toISOString(),
            },
          ],
          statistics: {
            by_status: [
              { status: "pending", count: 23 },
              { status: "in_progress", count: 15 },
              { status: "completed", count: 107 },
            ],
            by_result: [
              { result: "approved", count: 89 },
              { result: "rejected", count: 33 },
              { result: "pending review", count: 8 },
            ],
            processing_time: {
              average: 3.7,
              minimum: 1.2,
              maximum: 8.5,
            },
          },
          trends: [
            {
              period: "2024-01",
              requests_count: 42,
              evaluations_count: 38,
              approval_rate: 76.3,
              avg_processing_time: 4.2,
            },
            {
              period: "2024-02",
              requests_count: 51,
              evaluations_count: 47,
              approval_rate: 80.9,
              avg_processing_time: 3.8,
            },
          ],
        };
        this.is_loading = false;
      }, 1500);
    },
    exportReport(format) {
      const params = new URLSearchParams({
        format: format,
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        status: this.filters.status,
      });

      window.open(
        `/credit-analysis/evaluation/export?${params.toString()}`,
        "_blank"
      );
      success_message(this.$t("export started"));
    },
    formatDate(date) {
      return moment(date).format("DD/MM/YYYY HH:mm");
    },
    getStatusClass(status) {
      const classes = {
        pending: "is-warning",
        in_progress: "is-info",
        completed: "is-success",
      };
      return classes[status] || "is-light";
    },
    getResultClass(result) {
      const classes = {
        approved: "is-success",
        rejected: "is-danger",
        pending_review: "is-warning",
      };
      return classes[result] || "is-light";
    },
  },
  created() {
    this.generateReports();
  },
};
</script>

<style scoped>
.tab-content {
  margin-top: 1rem;
}

.box {
  margin-bottom: 1.5rem;
}

.level {
  margin-bottom: 0.5rem;
}

.table-container {
  overflow-x: auto;
}

.buttons {
  justify-content: center;
}
</style>
